import { useEffect, useState } from "react";
import { toast } from "sonner";
import {
  Plus, Trash2, Pencil, X, ExternalLink, Loader2, PackagePlus,
  Copy, FlaskConical, Search, Check, Mail, Send, Download, AlertTriangle, AlertCircle, Sparkles, BellRing, Tags as TagsIcon,
} from "lucide-react";
import { useLang } from "@/i18n";
import { api, resolveImage, formatApiErrorDetail, formatDate } from "@/lib/api";
import { TagPill, contrastText } from "@/lib/colors";
import { usePageTitle } from "@/lib/useTitle";
import { SiteHeader } from "@/components/SiteHeader";
import { Footer } from "@/components/Footer";
import { ImageManager } from "@/components/ImageManager";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { Checkbox } from "@/components/ui/checkbox";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent,
  AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle,
} from "@/components/ui/alert-dialog";

const FALLBACK_CATEGORIES = ["Maslá", "Oleje a maceráty", "Esenciálne oleje", "Farbivá a pigmenty", "Iné"];

const emptyBatch = () => ({ batch_code: "", product_name: "", production_date: "", images: [], ingredient_ids: [], ingredient_weights: {}, is_certified: false, check_dosage_calculation: true });
const emptyIngredient = () => ({ name: "", category: "", description: "", tag_ids: [], supplier_url: "", images: [], max_recommended_dosage_percent: "", allows_any_dosage: false });
const emptyTag = () => ({ name: "", color_hex: "#4CAF50", description: "", inheritance_mode: "ANY_PRESENCE", min_percentage_required: "" });

export default function AdminDashboard() {
  const { t } = useLang();
  usePageTitle(t("title.dashboard"));
  const [batches, setBatches] = useState([]);
  const [ingredients, setIngredients] = useState([]);
  const [categories, setCategories] = useState(FALLBACK_CATEGORIES);
  const [defaultCert, setDefaultCert] = useState(false);
  const [loading, setLoading] = useState(true);

  const [open, setOpen] = useState(false);
  const [editingId, setEditingId] = useState(null);
  const [form, setForm] = useState(emptyBatch());
  const [saving, setSaving] = useState(false);
  const [pickerSearch, setPickerSearch] = useState("");

  const [ingOpen, setIngOpen] = useState(false);
  const [ingEditingId, setIngEditingId] = useState(null);
  const [ingForm, setIngForm] = useState(emptyIngredient());
  const [ingSaving, setIngSaving] = useState(false);

  const [tags, setTags] = useState([]);
  const [tagOpen, setTagOpen] = useState(false);
  const [tagEditingId, setTagEditingId] = useState(null);
  const [tagForm, setTagForm] = useState(emptyTag());
  const [tagSaving, setTagSaving] = useState(false);
  const [deleteTag, setDeleteTag] = useState(null);

  const [deleteBatch, setDeleteBatch] = useState(null);
  const [deleteIng, setDeleteIng] = useState(null);

  const [counts, setCounts] = useState({ crisis: 0, marketing: 0, batch: 0 });
  const [campaigns, setCampaigns] = useState([]);
  const [emailForm, setEmailForm] = useState({ list_type: "crisis", batch_code: "", subject: "", body: "" });
  const [sending, setSending] = useState(false);

  const loadAll = () => {
    setLoading(true);
    Promise.all([api.get("/batches"), api.get("/ingredients"), api.get("/categories"), api.get("/admin/subscribers/counts"), api.get("/admin/email-campaigns"), api.get("/config"), api.get("/ingredient-tags")])
      .then(([b, i, c, sc, ec, cfg, tg]) => {
        setBatches(b.data);
        setIngredients(i.data);
        if (Array.isArray(c.data) && c.data.length) setCategories(c.data);
        setCounts(sc.data);
        setCampaigns(ec.data);
        setDefaultCert(!!cfg.data.default_certification);
        setTags(tg.data);
      })
      .finally(() => setLoading(false));
  };

  useEffect(() => { loadAll(); }, []);

  // ---------- Batch ----------
  const openCreate = () => { setEditingId(null); setForm({ ...emptyBatch(), is_certified: defaultCert }); setPickerSearch(""); setOpen(true); };
  const openEdit = (b) => {
    setEditingId(b.id);
    setForm({ batch_code: b.batch_code, product_name: b.product_name, production_date: b.production_date, images: b.images || [], ingredient_ids: b.ingredient_ids || [], ingredient_weights: b.ingredient_weights || {}, is_certified: !!b.is_certified, check_dosage_calculation: !!b.check_dosage_calculation });
    setPickerSearch(""); setOpen(true);
  };
  const openClone = (b) => {
    setEditingId(null);
    setForm({ batch_code: "", product_name: `${b.product_name} (kópia)`, production_date: "", images: (b.images || []).map((im) => ({ ...im })), ingredient_ids: b.ingredient_ids || [], ingredient_weights: { ...(b.ingredient_weights || {}) }, is_certified: !!b.is_certified, check_dosage_calculation: true });
    setPickerSearch(""); setOpen(true);
    toast.info(t("clone.toast"));
  };
  const setField = (k, v) => setForm((f) => ({ ...f, [k]: v }));
  const toggleIngredient = (id) => setForm((f) => {
    if (f.ingredient_ids.includes(id)) {
      const weights = { ...f.ingredient_weights };
      delete weights[id];
      return { ...f, ingredient_ids: f.ingredient_ids.filter((x) => x !== id), ingredient_weights: weights };
    }
    return { ...f, ingredient_ids: [...f.ingredient_ids, id] };
  });
  const setWeight = (id, value) => setForm((f) => {
    const weights = { ...f.ingredient_weights };
    if (value === "" || value === null) delete weights[id];
    else weights[id] = value;
    return { ...f, ingredient_weights: weights };
  });

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSaving(true);
    const cleanWeights = {};
    Object.entries(form.ingredient_weights).forEach(([id, v]) => {
      const n = parseFloat(v);
      if (!isNaN(n) && n > 0 && form.ingredient_ids.includes(id)) cleanWeights[id] = n;
    });
    const payload = { ...form, ingredient_weights: cleanWeights };
    try {
      if (editingId) { await api.put(`/batches/${editingId}`, payload); toast.success(t("toast.batchUpdated")); }
      else { await api.post("/batches", payload); toast.success(t("toast.batchPublished")); }
      setOpen(false); loadAll();
    } catch (err) { toast.error(formatApiErrorDetail(err.response?.data?.detail) || t("toast.saveFail")); }
    finally { setSaving(false); }
  };

  const confirmDeleteBatch = async () => {
    try { await api.delete(`/batches/${deleteBatch.id}`); toast.success(t("toast.batchDeleted")); setDeleteBatch(null); loadAll(); }
    catch { toast.error(t("toast.deleteFail")); }
  };

  // ---------- Ingredient ----------
  const openIngCreate = () => { setIngEditingId(null); setIngForm(emptyIngredient()); setIngOpen(true); };
  const openIngEdit = (ing) => {
    setIngEditingId(ing.id);
    setIngForm({ name: ing.name, category: ing.category, description: ing.description || "", tag_ids: ing.tag_ids || [], supplier_url: ing.supplier_url || "", images: ing.images || [], max_recommended_dosage_percent: ing.max_recommended_dosage_percent ?? "", allows_any_dosage: !!ing.allows_any_dosage });
    setIngOpen(true);
  };
  const openIngClone = (ing) => {
    setIngEditingId(null);
    setIngForm({ name: `${ing.name} (kópia)`, category: ing.category, description: ing.description || "", tag_ids: ing.tag_ids || [], supplier_url: ing.supplier_url || "", images: (ing.images || []).map((im) => ({ ...im })), max_recommended_dosage_percent: ing.max_recommended_dosage_percent ?? "", allows_any_dosage: !!ing.allows_any_dosage });
    setIngOpen(true);
    toast.info(t("clone.ing.toast"));
  };
  const setIngField = (k, v) => setIngForm((f) => ({ ...f, [k]: v }));
  const toggleIngTag = (id) => setIngForm((f) => ({ ...f, tag_ids: f.tag_ids.includes(id) ? f.tag_ids.filter((x) => x !== id) : [...f.tag_ids, id] }));

  const handleIngSubmit = async (e) => {
    e.preventDefault();
    if (!ingForm.category) { toast.error(t("toast.selectCategory")); return; }
    setIngSaving(true);
    const dose = parseFloat(ingForm.max_recommended_dosage_percent);
    const payload = {
      ...ingForm,
      max_recommended_dosage_percent: isNaN(dose) ? null : dose,
    };
    try {
      if (ingEditingId) { await api.put(`/ingredients/${ingEditingId}`, payload); toast.success(t("toast.ingUpdated")); }
      else { await api.post("/ingredients", payload); toast.success(t("toast.ingAdded")); }
      setIngOpen(false); loadAll();
    } catch (err) { toast.error(formatApiErrorDetail(err.response?.data?.detail) || t("toast.saveFail")); }
    finally { setIngSaving(false); }
  };

  const confirmDeleteIng = async () => {
    try { await api.delete(`/ingredients/${deleteIng.id}`); toast.success(t("toast.ingDeleted")); setDeleteIng(null); loadAll(); }
    catch { toast.error(t("toast.deleteFail")); }
  };

  // ---------- Tags ----------
  const openTagCreate = () => { setTagEditingId(null); setTagForm(emptyTag()); setTagOpen(true); };
  const openTagEdit = (tg) => { setTagEditingId(tg.id); setTagForm({ name: tg.name, color_hex: tg.color_hex || "#4CAF50", description: tg.description || "", inheritance_mode: tg.inheritance_mode || "ANY_PRESENCE", min_percentage_required: tg.min_percentage_required ?? "" }); setTagOpen(true); };
  const setTagField = (k, v) => setTagForm((f) => ({ ...f, [k]: v }));

  const handleTagSubmit = async (e) => {
    e.preventDefault();
    setTagSaving(true);
    const minPct = parseFloat(tagForm.min_percentage_required);
    const payload = {
      ...tagForm,
      min_percentage_required: tagForm.inheritance_mode === "PERCENTAGE_THRESHOLD" && !isNaN(minPct) ? minPct : null,
    };
    try {
      if (tagEditingId) { await api.put(`/ingredient-tags/${tagEditingId}`, payload); toast.success(t("toast.tagUpdated")); }
      else { await api.post("/ingredient-tags", payload); toast.success(t("toast.tagAdded")); }
      setTagOpen(false); loadAll();
    } catch (err) { toast.error(formatApiErrorDetail(err.response?.data?.detail) || t("toast.saveFail")); }
    finally { setTagSaving(false); }
  };

  const confirmDeleteTag = async () => {
    try { await api.delete(`/ingredient-tags/${deleteTag.id}`); toast.success(t("toast.tagDeleted")); setDeleteTag(null); loadAll(); }
    catch { toast.error(t("toast.deleteFail")); }
  };

  // ---------- Email ----------
  const setEmailField = (k, v) => setEmailForm((f) => ({ ...f, [k]: v }));

  const handleExportCSV = async (listType) => {
    try {
      const res = await api.get("/admin/subscribers", { params: { list_type: listType } });
      const rows = res.data;
      if (!rows.length) { toast.info(t("toast.exportEmpty")); return; }
      const headers = listType === "batch" ? ["email", "batch_code", "subscription_date"] : ["email", "subscription_date"];
      const csv = [headers.join(","), ...rows.map((r) => headers.map((h) => `"${(r[h] ?? "").toString().replace(/"/g, '""')}"`).join(","))].join("\n");
      const blob = new Blob([csv], { type: "text/csv;charset=utf-8;" });
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url; a.download = `${listType}-subscribers.csv`; a.click();
      URL.revokeObjectURL(url);
      toast.success(t("toast.exported", { n: rows.length }));
    } catch { toast.error(t("toast.exportFail")); }
  };

  const handleSendEmail = async (e) => {
    e.preventDefault();
    if (emailForm.list_type === "batch" && !emailForm.batch_code) { toast.error(t("toast.selectBatch")); return; }
    setSending(true);
    try {
      const res = await api.post("/admin/send-email", emailForm);
      toast.success(res.data.message);
      setEmailForm((f) => ({ ...f, subject: "", body: "" }));
    } catch (err) { toast.error(formatApiErrorDetail(err.response?.data?.detail) || t("toast.sendFail")); }
    finally { setSending(false); loadAll(); }
  };

  const groupByCategory = (list) => list.reduce((acc, ing) => { (acc[ing.category] = acc[ing.category] || []).push(ing); return acc; }, {});
  const filteredPicker = ingredients.filter((i) => i.name.toLowerCase().includes(pickerSearch.toLowerCase()));
  const pickerGrouped = groupByCategory(filteredPicker);
  const ingGrouped = groupByCategory(ingredients);
  const ingNameById = Object.fromEntries(ingredients.map((i) => [i.id, i.name]));

  return (
    <div className="min-h-screen bg-secondary/30">
      <SiteHeader />

      <div className="max-w-6xl mx-auto px-5 sm:px-8 py-10 sm:py-14">
        <div className="mb-8">
          <span className="font-data text-xs uppercase tracking-[0.2em] text-sage-dark font-semibold">{t("admin.center")}</span>
          <h1 className="font-heading text-4xl sm:text-5xl text-ink mt-1">{t("admin.dbtitle")}</h1>
        </div>

        <Tabs defaultValue="batches">
          <TabsList className="rounded-sm bg-white border border-border mb-8">
            <TabsTrigger value="batches" data-testid="tab-batches" className="rounded-sm font-semibold text-sm data-[state=active]:bg-ink data-[state=active]:text-white">{t("admin.tab.batches")}</TabsTrigger>
            <TabsTrigger value="ingredients" data-testid="tab-ingredients-admin" className="rounded-sm font-semibold text-sm data-[state=active]:bg-ink data-[state=active]:text-white">{t("admin.tab.ingredients")}</TabsTrigger>
            <TabsTrigger value="tags" data-testid="tab-tags-admin" className="rounded-sm font-semibold text-sm data-[state=active]:bg-ink data-[state=active]:text-white">{t("admin.tab.tags")}</TabsTrigger>
            <TabsTrigger value="emails" data-testid="tab-emails-admin" className="rounded-sm font-semibold text-sm data-[state=active]:bg-ink data-[state=active]:text-white">{t("admin.tab.emails")}</TabsTrigger>
          </TabsList>

          {/* BATCHES */}
          <TabsContent value="batches">
            <div className="flex justify-end mb-6">
              <Button onClick={openCreate} data-testid="add-batch-button" className="h-12 px-6 rounded-sm bg-ink text-white hover:bg-sage-dark gap-2 font-semibold">
                <Plus className="h-4 w-4" /> {t("admin.newBatch")}
              </Button>
            </div>
            {loading ? (
              <div className="space-y-3">{[...Array(3)].map((_, i) => (<div key={i} className="h-20 rounded-sm bg-white border border-border animate-pulse" />))}</div>
            ) : batches.length === 0 ? (
              <div className="text-center py-24 border border-dashed border-border rounded-sm bg-white" data-testid="admin-empty-state">
                <PackagePlus className="h-10 w-10 mx-auto text-gray-300" strokeWidth={2} />
                <p className="mt-4 font-heading text-2xl text-ink">{t("admin.noBatches")}</p>
              </div>
            ) : (
              <div className="space-y-3" data-testid="batch-list">
                {batches.map((b) => (
                  <div key={b.id} data-testid={`admin-batch-${b.batch_code}`} className="flex items-center gap-4 bg-white border border-border rounded-sm p-4 hover:border-sage transition-colors duration-200">
                    <div className="h-14 w-14 shrink-0 rounded-sm overflow-hidden bg-secondary">
                      {b.image_url && (<img src={resolveImage(b.image_url)} alt={b.product_name} className="h-full w-full object-cover" />)}
                    </div>
                    <div className="flex-1 min-w-0">
                      <span className="font-data text-xs uppercase tracking-[0.12em] text-sage-dark font-semibold">{b.batch_code}</span>
                      <p className="font-heading text-lg text-ink truncate leading-tight">{b.product_name}</p>
                      <span className="font-data text-xs text-gray-400">{formatDate(b.production_date)} · {(b.ingredient_ids || []).length} {t("admin.ingCount")}</span>
                      {b.inherited_tags && b.inherited_tags.length > 0 && (
                        <div className="mt-2 flex flex-wrap gap-1.5" data-testid={`admin-batch-tags-${b.batch_code}`}>
                          {b.inherited_tags.map((tag) => (<TagPill key={tag.id} tag={tag} testid={`admin-batch-tag-${b.batch_code}-${tag.id}`} />))}
                        </div>
                      )}
                    </div>
                    <div className="flex items-center gap-2">
                      <a href={`/batch/${b.batch_code}`} target="_blank" rel="noopener noreferrer" data-testid={`view-batch-${b.batch_code}`} className="h-9 w-9 flex items-center justify-center rounded-sm border border-border text-gray-500 hover:text-ink hover:border-ink transition-colors duration-200" title={t("admin.tip.public")}><ExternalLink className="h-4 w-4" /></a>
                      <button onClick={() => openClone(b)} data-testid={`clone-batch-${b.batch_code}`} className="h-9 w-9 flex items-center justify-center rounded-sm border border-border text-gray-500 hover:text-sage-dark hover:border-sage transition-colors duration-200" title={t("admin.tip.clone")}><Copy className="h-4 w-4" /></button>
                      <button onClick={() => openEdit(b)} data-testid={`edit-batch-${b.batch_code}`} className="h-9 w-9 flex items-center justify-center rounded-sm border border-border text-gray-500 hover:text-ink hover:border-ink transition-colors duration-200" title={t("admin.tip.edit")}><Pencil className="h-4 w-4" /></button>
                      <button onClick={() => setDeleteBatch(b)} data-testid={`delete-batch-${b.batch_code}`} className="h-9 w-9 flex items-center justify-center rounded-sm border border-border text-gray-500 hover:text-destructive hover:border-destructive transition-colors duration-200" title={t("admin.tip.delete")}><Trash2 className="h-4 w-4" /></button>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </TabsContent>

          {/* INGREDIENTS */}
          <TabsContent value="ingredients">
            <div className="flex justify-end mb-6">
              <Button onClick={openIngCreate} data-testid="add-ingredient-admin-button" className="h-12 px-6 rounded-sm bg-ink text-white hover:bg-sage-dark gap-2 font-semibold">
                <Plus className="h-4 w-4" /> {t("admin.newIngredient")}
              </Button>
            </div>
            {loading ? (
              <div className="space-y-3">{[...Array(4)].map((_, i) => (<div key={i} className="h-14 rounded-sm bg-white border border-border animate-pulse" />))}</div>
            ) : ingredients.length === 0 ? (
              <div className="text-center py-24 border border-dashed border-border rounded-sm bg-white">
                <FlaskConical className="h-10 w-10 mx-auto text-gray-300" strokeWidth={2} />
                <p className="mt-4 font-heading text-2xl text-ink">{t("admin.noIngredients")}</p>
              </div>
            ) : (
              <div className="space-y-10" data-testid="admin-ingredient-list">
                {categories.filter((c) => ingGrouped[c]?.length).map((cat) => (
                  <div key={cat}>
                    <div className="flex items-baseline gap-3 mb-3">
                      <h3 className="font-heading text-2xl text-ink">{cat}</h3>
                      <span className="font-data text-xs text-gray-400">{ingGrouped[cat].length}</span>
                    </div>
                    <div className="space-y-2">
                      {ingGrouped[cat].map((ing) => (
                        <div key={ing.id} data-testid={`admin-ingredient-${ing.id}`} className="flex items-center gap-4 bg-white border border-border rounded-sm p-4 hover:border-sage transition-colors duration-200">
                          <div className="flex-1 min-w-0">
                            <p className="font-data text-sm text-ink">{ing.name}</p>
                            <div className="flex items-center gap-2 mt-1 flex-wrap">
                              {(ing.tags || []).map((tg) => (<TagPill key={tg.id} tag={tg} />))}
                              {ing.description && (<span className="text-xs text-gray-400 truncate max-w-md">{ing.description}</span>)}
                            </div>
                          </div>
                          <div className="flex items-center gap-2">
                            <a href={`/ingredient/${ing.id}`} target="_blank" rel="noopener noreferrer" data-testid={`view-ingredient-${ing.id}`} className="h-9 w-9 flex items-center justify-center rounded-sm border border-border text-gray-500 hover:text-ink hover:border-ink transition-colors duration-200" title={t("admin.tip.public")}><ExternalLink className="h-4 w-4" /></a>
                            <button onClick={() => openIngClone(ing)} data-testid={`clone-ingredient-${ing.id}`} className="h-9 w-9 flex items-center justify-center rounded-sm border border-border text-gray-500 hover:text-sage-dark hover:border-sage transition-colors duration-200" title={t("admin.tip.cloneIng")}><Copy className="h-4 w-4" /></button>
                            <button onClick={() => openIngEdit(ing)} data-testid={`edit-ingredient-${ing.id}`} className="h-9 w-9 flex items-center justify-center rounded-sm border border-border text-gray-500 hover:text-ink hover:border-ink transition-colors duration-200" title={t("admin.tip.edit")}><Pencil className="h-4 w-4" /></button>
                            <button onClick={() => setDeleteIng(ing)} data-testid={`delete-ingredient-${ing.id}`} className="h-9 w-9 flex items-center justify-center rounded-sm border border-border text-gray-500 hover:text-destructive hover:border-destructive transition-colors duration-200" title={t("admin.tip.delete")}><Trash2 className="h-4 w-4" /></button>
                          </div>
                        </div>
                      ))}
                    </div>
                  </div>
                ))}
              </div>
            )}
          </TabsContent>

          {/* TAGS */}
          <TabsContent value="tags">
            <div className="flex justify-end mb-6">
              <Button onClick={openTagCreate} data-testid="add-tag-button" className="h-12 px-6 rounded-sm bg-ink text-white hover:bg-sage-dark gap-2 font-semibold">
                <Plus className="h-4 w-4" /> {t("admin.newTag")}
              </Button>
            </div>
            {loading ? (
              <div className="space-y-3">{[...Array(3)].map((_, i) => (<div key={i} className="h-14 rounded-sm bg-white border border-border animate-pulse" />))}</div>
            ) : tags.length === 0 ? (
              <div className="text-center py-24 border border-dashed border-border rounded-sm bg-white">
                <TagsIcon className="h-10 w-10 mx-auto text-gray-300" strokeWidth={2} />
                <p className="mt-4 font-heading text-2xl text-ink">{t("admin.noTags")}</p>
              </div>
            ) : (
              <div className="flex flex-wrap gap-3" data-testid="admin-tag-list">
                {tags.map((tg) => (
                  <div key={tg.id} data-testid={`admin-tag-${tg.id}`} className="flex items-center gap-3 bg-white border border-border rounded-sm p-3">
                    <TagPill tag={tg} />
                    <span className="font-data text-xs text-gray-400">{tg.color_hex}</span>
                    <div className="flex items-center gap-1">
                      <button onClick={() => openTagEdit(tg)} data-testid={`edit-tag-${tg.id}`} className="h-8 w-8 flex items-center justify-center rounded-sm border border-border text-gray-500 hover:text-ink hover:border-ink transition-colors duration-200" title={t("admin.tip.edit")}><Pencil className="h-4 w-4" /></button>
                      <button onClick={() => setDeleteTag(tg)} data-testid={`delete-tag-${tg.id}`} className="h-8 w-8 flex items-center justify-center rounded-sm border border-border text-gray-500 hover:text-destructive hover:border-destructive transition-colors duration-200" title={t("admin.tip.delete")}><Trash2 className="h-4 w-4" /></button>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </TabsContent>

          {/* EMAILS */}
          <TabsContent value="emails">
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-10">
              {[
                { key: "crisis", label: t("em.count.crisis"), icon: AlertTriangle, accent: "text-amber-700 bg-amber-100" },
                { key: "marketing", label: t("em.count.marketing"), icon: Sparkles, accent: "text-sage-dark bg-sage/15" },
                { key: "batch", label: t("em.count.batch"), icon: BellRing, accent: "text-ink bg-secondary" },
              ].map((c) => (
                <div key={c.key} data-testid={`count-card-${c.key}`} className="p-6 bg-white border border-border rounded-sm">
                  <div className="flex items-center justify-between">
                    <span className={`inline-flex h-10 w-10 items-center justify-center rounded-sm ${c.accent}`}><c.icon className="h-5 w-5" strokeWidth={2} /></span>
                    <span className="font-heading text-5xl text-ink" data-testid={`count-${c.key}`}>{counts[c.key]}</span>
                  </div>
                  <p className="mt-4 font-data text-xs uppercase tracking-[0.12em] text-gray-600 font-semibold">{c.label}</p>
                  <Button variant="outline" onClick={() => handleExportCSV(c.key)} data-testid={`export-${c.key}-button`} className="mt-4 w-full h-10 rounded-sm gap-2 text-sm font-semibold">
                    <Download className="h-4 w-4" /> {t("em.export")}
                  </Button>
                </div>
              ))}
            </div>

            <div className="bg-white border border-border rounded-sm p-6 sm:p-8 max-w-3xl">
              <div className="flex items-center gap-2 mb-1">
                <Mail className="h-5 w-5 text-sage-dark" strokeWidth={2} />
                <h2 className="font-heading text-2xl sm:text-3xl text-ink">{t("em.title")}</h2>
              </div>
              <p className="text-sm text-gray-600 mb-6">
                {t("em.privacy")} <span className="text-amber-700 font-semibold">{t("em.mockNote")}</span>
              </p>

              <form onSubmit={handleSendEmail} className="space-y-5">
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                  <div className="space-y-2">
                    <Label className="font-data text-xs uppercase tracking-[0.12em] text-gray-600 font-semibold">{t("em.target")}</Label>
                    <Select value={emailForm.list_type} onValueChange={(v) => setEmailField("list_type", v)}>
                      <SelectTrigger data-testid="email-list-select" className="h-11 rounded-sm focus:ring-sage"><SelectValue /></SelectTrigger>
                      <SelectContent>
                        <SelectItem value="crisis" data-testid="list-option-crisis">{t("em.opt.crisis")}</SelectItem>
                        <SelectItem value="marketing" data-testid="list-option-marketing">{t("em.opt.marketing")}</SelectItem>
                        <SelectItem value="batch" data-testid="list-option-batch">{t("em.opt.batch")}</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                  {emailForm.list_type === "batch" && (
                    <div className="space-y-2">
                      <Label className="font-data text-xs uppercase tracking-[0.12em] text-gray-600 font-semibold">{t("em.batch")}</Label>
                      <Select value={emailForm.batch_code} onValueChange={(v) => setEmailField("batch_code", v)}>
                        <SelectTrigger data-testid="email-batch-select" className="h-11 rounded-sm focus:ring-sage"><SelectValue placeholder={t("em.selectBatch")} /></SelectTrigger>
                        <SelectContent>
                          {batches.map((b) => (<SelectItem key={b.id} value={b.batch_code} data-testid={`batch-option-${b.batch_code}`}>{b.batch_code} — {b.product_name}</SelectItem>))}
                        </SelectContent>
                      </Select>
                    </div>
                  )}
                </div>

                <div className="space-y-2">
                  <Label className="font-data text-xs uppercase tracking-[0.12em] text-gray-600 font-semibold">{t("em.subject")}</Label>
                  <Input required value={emailForm.subject} onChange={(e) => setEmailField("subject", e.target.value)} placeholder={t("em.subjectPlaceholder")} data-testid="email-subject-input" className="h-11 rounded-sm focus-visible:ring-sage focus-visible:ring-2 focus-visible:border-sage" />
                </div>
                <div className="space-y-2">
                  <Label className="font-data text-xs uppercase tracking-[0.12em] text-gray-600 font-semibold">{t("em.body")}</Label>
                  <Textarea required value={emailForm.body} onChange={(e) => setEmailField("body", e.target.value)} placeholder={t("em.bodyPlaceholder")} rows={7} data-testid="email-body-input" className="rounded-sm focus-visible:ring-sage focus-visible:ring-2 focus-visible:border-sage" />
                </div>
                <Button type="submit" disabled={sending} data-testid="send-email-button" className="h-12 px-7 rounded-sm bg-ink text-white hover:bg-sage-dark font-semibold gap-2">
                  {sending ? <Loader2 className="h-4 w-4 animate-spin" /> : <Send className="h-4 w-4" />} {t("em.send")}
                </Button>
              </form>
            </div>

            {/* Send history */}
            <div className="bg-white border border-border rounded-sm p-6 sm:p-8 max-w-3xl mt-6" data-testid="email-history">
              <h2 className="font-heading text-2xl text-ink mb-4">{t("em.history.title")}</h2>
              {campaigns.length === 0 ? (
                <p className="text-sm text-gray-500">{t("em.history.empty")}</p>
              ) : (
                <div className="divide-y divide-border">
                  {campaigns.map((c) => {
                    const statusColor =
                      c.status === "sent" ? "bg-sage/15 text-sage-dark"
                        : c.status === "partial" ? "bg-amber-100 text-amber-700"
                        : "bg-red-100 text-red-700";
                    return (
                      <div key={c.id} className="py-3 flex items-center gap-4" data-testid={`campaign-${c.id}`}>
                        <span className={`px-2 py-0.5 rounded-full text-xs font-bold font-data shrink-0 ${statusColor}`}>
                          {t(`em.status.${c.status}`)}
                        </span>
                        <div className="flex-1 min-w-0">
                          <p className="text-sm font-semibold text-ink truncate">{c.subject}</p>
                          <span className="font-data text-xs text-gray-400">
                            {c.list_type}{c.batch_code ? ` · ${c.batch_code}` : ""} · {(c.sent ?? 0)}/{c.recipient_count} · {formatDate(c.sent_at)}
                          </span>
                        </div>
                      </div>
                    );
                  })}
                </div>
              )}
            </div>
          </TabsContent>
        </Tabs>
      </div>

      {/* BATCH FORM DIALOG */}
      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto rounded-sm">
          <DialogHeader>
            <DialogTitle className="font-heading text-2xl sm:text-3xl">{editingId ? t("bd.editTitle") : t("bd.createTitle")}</DialogTitle>
            <DialogDescription className="text-gray-500">{t("bd.desc")}</DialogDescription>
          </DialogHeader>
          <form onSubmit={handleSubmit} className="space-y-6 mt-2">
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label className="font-data text-xs uppercase tracking-[0.12em] text-gray-600 font-semibold">{t("bd.code")}</Label>
                <Input required data-testid="form-batch-code" value={form.batch_code} onChange={(e) => setField("batch_code", e.target.value)} placeholder="BATCH-2026-01" className="h-11 rounded-sm font-data focus-visible:ring-sage focus-visible:ring-2 focus-visible:border-sage" />
              </div>
              <div className="space-y-2">
                <Label className="font-data text-xs uppercase tracking-[0.12em] text-gray-600 font-semibold">{t("bd.date")}</Label>
                <Input required type="date" data-testid="form-production-date" value={form.production_date} onChange={(e) => setField("production_date", e.target.value)} className="h-11 rounded-sm font-data focus-visible:ring-sage focus-visible:ring-2 focus-visible:border-sage" />
              </div>
            </div>
            <div className="space-y-2">
              <Label className="font-data text-xs uppercase tracking-[0.12em] text-gray-600 font-semibold">{t("bd.name")}</Label>
              <Input required data-testid="form-product-name" value={form.product_name} onChange={(e) => setField("product_name", e.target.value)} placeholder="Šľahané bambucké maslo" className="h-11 rounded-sm focus-visible:ring-sage focus-visible:ring-2 focus-visible:border-sage" />
            </div>
            <div className="space-y-2">
              <Label className="font-data text-xs uppercase tracking-[0.12em] text-gray-600 font-semibold">{t("bd.images")}</Label>
              <ImageManager images={form.images} onChange={(imgs) => setField("images", imgs)} testid="batch-image-manager" />
            </div>
            <div className="flex items-center gap-3 p-4 rounded-sm border border-border bg-secondary/30">
              <Checkbox id="cert-checkbox" checked={form.is_certified} onCheckedChange={(v) => setField("is_certified", !!v)} data-testid="form-certified-checkbox" />
              <Label htmlFor="cert-checkbox" className="text-sm font-semibold text-ink cursor-pointer">{t("bd.certified")}</Label>
            </div>
            <div className="flex items-center gap-3 p-4 rounded-sm border border-border bg-secondary/30">
              <Checkbox id="dosage-checkbox" checked={form.check_dosage_calculation} onCheckedChange={(v) => setField("check_dosage_calculation", !!v)} data-testid="form-check-dosage-checkbox" />
              <Label htmlFor="dosage-checkbox" className="text-sm font-semibold text-ink cursor-pointer">{t("bd.checkDosage")}</Label>
            </div>

            <div className="space-y-3">
              <Label className="font-data text-xs uppercase tracking-[0.12em] text-gray-600 font-semibold">{t("bd.ingredients")} ({form.ingredient_ids.length} {t("bd.selected")})</Label>
              {ingredients.length === 0 ? (
                <p className="text-sm text-gray-500 p-4 border border-dashed border-border rounded-sm">{t("bd.noIngHint")}</p>
              ) : (
                <div className="border border-border rounded-sm">
                  <div className="relative p-2 border-b border-border">
                    <Search className="absolute left-4 top-1/2 -translate-y-1/2 h-4 w-4 text-gray-400" />
                    <Input value={pickerSearch} onChange={(e) => setPickerSearch(e.target.value)} placeholder={t("bd.searchIng")} data-testid="ingredient-picker-search" className="h-9 pl-9 rounded-sm border-transparent focus-visible:ring-sage focus-visible:ring-1" />
                  </div>
                  <div className="max-h-64 overflow-y-auto p-2 space-y-3">
                    {categories.filter((c) => pickerGrouped[c]?.length).map((cat) => (
                      <div key={cat}>
                        <p className="font-data text-xs uppercase tracking-[0.12em] text-sage-dark px-2 py-1 font-semibold">{cat}</p>
                        <div className="space-y-1">
                          {pickerGrouped[cat].map((ing) => {
                            const checked = form.ingredient_ids.includes(ing.id);
                            return (
                              <button type="button" key={ing.id} onClick={() => toggleIngredient(ing.id)} data-testid={`picker-ingredient-${ing.id}`} className={`w-full flex items-center gap-3 px-2 py-2 rounded-sm text-left transition-colors duration-200 ${checked ? "bg-sage/10" : "hover:bg-secondary"}`}>
                                <span className={`h-5 w-5 shrink-0 rounded-sm border flex items-center justify-center ${checked ? "bg-sage border-sage text-white" : "border-gray-300"}`}>{checked && <Check className="h-3.5 w-3.5" />}</span>
                                <span className="font-data text-sm text-ink flex-1">{ing.name}</span>
                                {ing.bio_status && (<span className="text-xs text-gray-400">{ing.bio_status}</span>)}
                              </button>
                            );
                          })}
                        </div>
                      </div>
                    ))}
                    {filteredPicker.length === 0 && (<p className="text-sm text-gray-400 text-center py-4">{t("bd.notFound")}</p>)}
                  </div>
                </div>
              )}
              {form.ingredient_ids.length > 0 && (() => {
                const total = form.ingredient_ids.reduce((s, id) => { const n = parseFloat(form.ingredient_weights[id]); return s + (isNaN(n) || n <= 0 ? 0 : n); }, 0);
                const dosageOf = (id, pct) => {
                  if (!form.check_dosage_calculation) return null;
                  const obj = ingredients.find((x) => x.id === id);
                  if (!obj) return null;
                  if (obj.allows_any_dosage) return "ok";
                  const max = obj.max_recommended_dosage_percent;
                  if (max === null || max === undefined || max === "") return "undefined";
                  if (pct != null && parseFloat(pct) > parseFloat(max)) return "exceeded";
                  return "ok";
                };
                const exceededNames = form.ingredient_ids.filter((id) => {
                  const n = parseFloat(form.ingredient_weights[id]);
                  const pct = !isNaN(n) && n > 0 && total > 0 ? ((n / total) * 100).toFixed(1) : null;
                  return dosageOf(id, pct) === "exceeded";
                }).map((id) => ingNameById[id]);
                return (
                  <div className="space-y-2">
                    {form.ingredient_ids.map((id) => {
                      const raw = form.ingredient_weights[id];
                      const n = parseFloat(raw);
                      const valid = !isNaN(n) && n > 0;
                      const pct = valid && total > 0 ? ((n / total) * 100).toFixed(1) : null;
                      const dstatus = dosageOf(id, pct);
                      return (
                        <div key={id} data-testid={`selected-ingredient-${id}`} className={`flex items-center gap-3 p-2.5 rounded-sm border bg-secondary/30 ${dstatus === "exceeded" ? "border-red-300" : "border-border"}`}>
                          <div className="flex-1 min-w-0">
                            <span className="flex items-center gap-1.5 font-data text-sm text-ink truncate">
                              {ingNameById[id] || "?"}
                              <a href={`/ingredient/${id}`} target="_blank" rel="noopener noreferrer" data-testid={`open-ingredient-${id}`} title={t("admin.tip.openIng")} onClick={(e) => e.stopPropagation()} className="text-gray-400 hover:text-sage-dark transition-colors duration-200 shrink-0"><ExternalLink className="h-3.5 w-3.5" /></a>
                            </span>
                            {dstatus === "exceeded" && (
                              <span data-testid={`dosage-preview-${id}`} className="inline-flex items-center gap-1 mt-0.5 px-1.5 py-0.5 rounded-full bg-red-100 text-red-700 text-[10px] font-bold font-data"><AlertTriangle className="h-2.5 w-2.5" /> {t("dosage.exceeded.short")}</span>
                            )}
                            {dstatus === "undefined" && (
                              <span data-testid={`dosage-preview-${id}`} className="inline-flex items-center gap-1 mt-0.5 px-1.5 py-0.5 rounded-full bg-amber-100 text-amber-700 text-[10px] font-bold font-data"><AlertCircle className="h-2.5 w-2.5" /> {t("dosage.undefined")}</span>
                            )}
                          </div>
                          <span className="font-data text-xs text-sage-dark w-16 text-right shrink-0" data-testid={`percent-${id}`}>{pct != null ? `${pct}%` : t("batch.percent.unknown")}</span>
                          <div className="relative shrink-0">
                            <Input type="number" min="0" step="any" value={raw ?? ""} onChange={(e) => setWeight(id, e.target.value)} placeholder={t("bd.weight")} data-testid={`weight-input-${id}`} className="h-9 w-28 rounded-sm font-data text-sm pr-7 focus-visible:ring-sage focus-visible:ring-1" />
                            <span className="absolute right-2 top-1/2 -translate-y-1/2 text-xs text-gray-400">g</span>
                          </div>
                          <button type="button" onClick={() => toggleIngredient(id)} className="text-gray-400 hover:text-destructive shrink-0"><X className="h-4 w-4" /></button>
                        </div>
                      );
                    })}
                    {total > 0 && (<div className="flex justify-end text-xs font-data text-gray-500 pt-1">{t("bd.totalWeight")}: {total.toFixed(2)} g</div>)}
                    {exceededNames.length > 0 && (
                      <div data-testid="dosage-preview-alert" className="flex gap-2 items-start p-3 rounded-sm border-2 border-red-300 bg-red-50">
                        <AlertTriangle className="h-4 w-4 text-red-600 shrink-0 mt-0.5" strokeWidth={2} />
                        <p className="text-xs font-bold text-red-800">{t("dosage.exceeded.box")} <span className="font-normal text-red-700">({exceededNames.join(", ")})</span></p>
                      </div>
                    )}
                  </div>
                );
              })()}
            </div>
            <div className="flex gap-3 pt-2">
              <Button type="submit" disabled={saving} data-testid="publish-button" className="flex-1 h-12 rounded-sm bg-ink text-white hover:bg-sage-dark font-semibold">
                {saving ? <Loader2 className="h-4 w-4 animate-spin" /> : editingId ? t("bd.save") : t("bd.publish")}
              </Button>
              <Button type="button" variant="outline" onClick={() => setOpen(false)} className="h-12 rounded-sm font-semibold">{t("common.cancel")}</Button>
            </div>
          </form>
        </DialogContent>
      </Dialog>

      {/* INGREDIENT FORM DIALOG */}
      <Dialog open={ingOpen} onOpenChange={setIngOpen}>
        <DialogContent className="max-w-lg max-h-[90vh] overflow-y-auto rounded-sm">
          <DialogHeader>
            <DialogTitle className="font-heading text-2xl sm:text-3xl">{ingEditingId ? t("id.editTitle") : t("id.createTitle")}</DialogTitle>
            <DialogDescription className="text-gray-500">{t("id.desc")}</DialogDescription>
          </DialogHeader>
          <form onSubmit={handleIngSubmit} className="space-y-5 mt-2">
            <div className="space-y-2">
              <Label className="font-data text-xs uppercase tracking-[0.12em] text-gray-600 font-semibold">{t("id.name")}</Label>
              <Input required data-testid="ing-form-name" value={ingForm.name} onChange={(e) => setIngField("name", e.target.value)} className="h-11 rounded-sm font-data focus-visible:ring-sage focus-visible:ring-2 focus-visible:border-sage" />
            </div>
            <div className="space-y-2">
              <Label className="font-data text-xs uppercase tracking-[0.12em] text-gray-600 font-semibold">{t("id.category")}</Label>
              <Select value={ingForm.category} onValueChange={(v) => setIngField("category", v)}>
                <SelectTrigger data-testid="ing-form-category" className="h-11 rounded-sm focus:ring-sage"><SelectValue placeholder={t("id.selectCategory")} /></SelectTrigger>
                <SelectContent>{categories.map((c) => (<SelectItem key={c} value={c} data-testid={`category-option-${c}`}>{c}</SelectItem>))}</SelectContent>
              </Select>
            </div>
            <div className="space-y-2">
              <Label className="font-data text-xs uppercase tracking-[0.12em] text-gray-600 font-semibold">{t("id.description")}</Label>
              <Textarea data-testid="ing-form-description" value={ingForm.description} onChange={(e) => setIngField("description", e.target.value)} placeholder={t("id.descPlaceholder")} rows={3} className="rounded-sm focus-visible:ring-sage focus-visible:ring-2 focus-visible:border-sage" />
            </div>
            <div className="space-y-2">
              <Label className="font-data text-xs uppercase tracking-[0.12em] text-gray-600 font-semibold">{t("id.tags")}</Label>
              {tags.length === 0 ? (
                <p className="text-sm text-gray-500 p-3 border border-dashed border-border rounded-sm">{t("id.noTags")}</p>
              ) : (
                <div className="flex flex-wrap gap-2 p-3 border border-border rounded-sm">
                  {tags.map((tg) => {
                    const active = ingForm.tag_ids.includes(tg.id);
                    return (
                      <button
                        type="button"
                        key={tg.id}
                        onClick={() => toggleIngTag(tg.id)}
                        data-testid={`ing-tag-toggle-${tg.id}`}
                        className={`inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-xs font-bold font-data transition-all duration-200 ${active ? "ring-2 ring-offset-1 ring-ink" : "opacity-50 hover:opacity-100"}`}
                        style={{ backgroundColor: tg.color_hex, color: contrastText(tg.color_hex) }}
                      >
                        {active && <Check className="h-3 w-3" />} {tg.name}
                      </button>
                    );
                  })}
                </div>
              )}
            </div>
            <div className="space-y-2">
              <Label className="font-data text-xs uppercase tracking-[0.12em] text-gray-600 font-semibold">{t("id.url")}</Label>
              <Input data-testid="ing-form-url" value={ingForm.supplier_url} onChange={(e) => setIngField("supplier_url", e.target.value)} placeholder="https://…" className="h-11 rounded-sm font-data text-sm focus-visible:ring-sage focus-visible:ring-2 focus-visible:border-sage" />
            </div>
            <div className="space-y-2">
              <Label className="font-data text-xs uppercase tracking-[0.12em] text-gray-600 font-semibold">{t("id.images")}</Label>
              <ImageManager images={ingForm.images} onChange={(imgs) => setIngField("images", imgs)} testid="ing-image-manager" />
            </div>
            <div className="space-y-3 p-4 rounded-sm border border-border bg-secondary/30">
              <div className="space-y-2">
                <Label className="font-data text-xs uppercase tracking-[0.12em] text-gray-600 font-semibold">{t("id.dosage")}</Label>
                <div className="relative w-40">
                  <Input type="number" min="0" step="any" disabled={ingForm.allows_any_dosage} data-testid="ing-form-dosage" value={ingForm.max_recommended_dosage_percent} onChange={(e) => setIngField("max_recommended_dosage_percent", e.target.value)} placeholder={t("id.dosagePlaceholder")} className="h-11 rounded-sm font-data text-sm pr-7 focus-visible:ring-sage focus-visible:ring-2 focus-visible:border-sage disabled:opacity-50" />
                  <span className="absolute right-3 top-1/2 -translate-y-1/2 text-xs text-gray-400">%</span>
                </div>
              </div>
              <div className="flex items-center gap-3">
                <Checkbox id="any-dosage-checkbox" checked={ingForm.allows_any_dosage} onCheckedChange={(v) => setIngField("allows_any_dosage", !!v)} data-testid="ing-form-any-dosage" />
                <Label htmlFor="any-dosage-checkbox" className="text-sm font-semibold text-ink cursor-pointer">{t("id.anyDosage")}</Label>
              </div>
            </div>
            <div className="flex gap-3 pt-2">
              <Button type="submit" disabled={ingSaving} data-testid="save-ingredient-button" className="flex-1 h-12 rounded-sm bg-ink text-white hover:bg-sage-dark font-semibold">
                {ingSaving ? <Loader2 className="h-4 w-4 animate-spin" /> : ingEditingId ? t("id.save") : t("id.add")}
              </Button>
              <Button type="button" variant="outline" onClick={() => setIngOpen(false)} className="h-12 rounded-sm font-semibold">{t("common.cancel")}</Button>
            </div>
          </form>
        </DialogContent>
      </Dialog>

      {/* DELETE DIALOGS */}
      <AlertDialog open={!!deleteBatch} onOpenChange={(o) => !o && setDeleteBatch(null)}>
        <AlertDialogContent className="rounded-sm">
          <AlertDialogHeader>
            <AlertDialogTitle className="font-heading text-2xl">{t("del.batch.title")}</AlertDialogTitle>
            <AlertDialogDescription>„{deleteBatch?.product_name}" ({deleteBatch?.batch_code}) — {t("del.batch.desc")}</AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel className="rounded-sm">{t("common.cancel")}</AlertDialogCancel>
            <AlertDialogAction onClick={confirmDeleteBatch} data-testid="confirm-delete-button" className="rounded-sm bg-destructive hover:bg-destructive/90">{t("del.confirm")}</AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      <AlertDialog open={!!deleteIng} onOpenChange={(o) => !o && setDeleteIng(null)}>
        <AlertDialogContent className="rounded-sm">
          <AlertDialogHeader>
            <AlertDialogTitle className="font-heading text-2xl">{t("del.ing.title")}</AlertDialogTitle>
            <AlertDialogDescription>„{deleteIng?.name}" — {t("del.ing.desc")}</AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel className="rounded-sm">{t("common.cancel")}</AlertDialogCancel>
            <AlertDialogAction onClick={confirmDeleteIng} data-testid="confirm-delete-ingredient-button" className="rounded-sm bg-destructive hover:bg-destructive/90">{t("del.confirm")}</AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      {/* TAG FORM DIALOG */}
      <Dialog open={tagOpen} onOpenChange={setTagOpen}>
        <DialogContent className="max-w-md rounded-sm">
          <DialogHeader>
            <DialogTitle className="font-heading text-2xl sm:text-3xl">{tagEditingId ? t("tag.editTitle") : t("tag.createTitle")}</DialogTitle>
          </DialogHeader>
          <form onSubmit={handleTagSubmit} className="space-y-5 mt-2">
            <div className="space-y-2">
              <Label className="font-data text-xs uppercase tracking-[0.12em] text-gray-600 font-semibold">{t("tag.name")}</Label>
              <Input required data-testid="tag-form-name" value={tagForm.name} onChange={(e) => setTagField("name", e.target.value)} placeholder="BIO" className="h-11 rounded-sm font-data focus-visible:ring-sage focus-visible:ring-2 focus-visible:border-sage" />
            </div>
            <div className="space-y-2">
              <Label className="font-data text-xs uppercase tracking-[0.12em] text-gray-600 font-semibold">{t("tag.color")}</Label>
              <div className="flex items-center gap-3">
                <input type="color" data-testid="tag-form-color" value={tagForm.color_hex} onChange={(e) => setTagField("color_hex", e.target.value)} className="h-11 w-14 rounded-sm border border-border cursor-pointer bg-white" />
                <Input data-testid="tag-form-hex" value={tagForm.color_hex} onChange={(e) => setTagField("color_hex", e.target.value)} placeholder="#4CAF50" className="h-11 w-32 rounded-sm font-data text-sm focus-visible:ring-sage focus-visible:ring-2 focus-visible:border-sage" />
                <div className="flex items-center gap-2 ml-auto">
                  <span className="text-xs text-gray-400">{t("tag.preview")}:</span>
                  <span className="inline-block px-2.5 py-0.5 rounded-full text-xs font-bold font-data" style={{ backgroundColor: tagForm.color_hex, color: contrastText(tagForm.color_hex) }}>
                    {tagForm.name || "BIO"}
                  </span>
                </div>
              </div>
            </div>
            <div className="space-y-2">
              <Label className="font-data text-xs uppercase tracking-[0.12em] text-gray-600 font-semibold">{t("tag.description")}</Label>
              <Textarea data-testid="tag-form-description" value={tagForm.description} onChange={(e) => setTagField("description", e.target.value)} placeholder={t("tag.descPlaceholder")} rows={3} className="rounded-sm focus-visible:ring-sage focus-visible:ring-2 focus-visible:border-sage" />
            </div>
            <div className="space-y-3 p-4 rounded-sm border border-border bg-secondary/30">
              <div className="space-y-2">
                <Label className="font-data text-xs uppercase tracking-[0.12em] text-gray-600 font-semibold">{t("tag.inheritance")}</Label>
                <Select value={tagForm.inheritance_mode} onValueChange={(v) => setTagField("inheritance_mode", v)}>
                  <SelectTrigger data-testid="tag-form-inheritance" className="h-11 rounded-sm font-data text-sm focus:ring-sage"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="ANY_PRESENCE" data-testid="inheritance-any">{t("tag.inheritance.any")}</SelectItem>
                    <SelectItem value="PERCENTAGE_THRESHOLD" data-testid="inheritance-threshold">{t("tag.inheritance.threshold")}</SelectItem>
                  </SelectContent>
                </Select>
                <p className="text-xs text-gray-500 leading-snug">{t(tagForm.inheritance_mode === "PERCENTAGE_THRESHOLD" ? "tag.inheritance.hint.threshold" : "tag.inheritance.hint.any")}</p>
              </div>
              {tagForm.inheritance_mode === "PERCENTAGE_THRESHOLD" && (
                <div className="space-y-2">
                  <Label className="font-data text-xs uppercase tracking-[0.12em] text-gray-600 font-semibold">{t("tag.minPercent")}</Label>
                  <div className="relative w-40">
                    <Input type="number" min="0" max="100" step="any" data-testid="tag-form-min-percent" value={tagForm.min_percentage_required} onChange={(e) => setTagField("min_percentage_required", e.target.value)} placeholder="98" className="h-11 rounded-sm font-data text-sm pr-7 focus-visible:ring-sage focus-visible:ring-2 focus-visible:border-sage" />
                    <span className="absolute right-3 top-1/2 -translate-y-1/2 text-xs text-gray-400">%</span>
                  </div>
                </div>
              )}
            </div>
            <div className="flex gap-3 pt-2">
              <Button type="submit" disabled={tagSaving} data-testid="save-tag-button" className="flex-1 h-12 rounded-sm bg-ink text-white hover:bg-sage-dark font-semibold">
                {tagSaving ? <Loader2 className="h-4 w-4 animate-spin" /> : tagEditingId ? t("tag.save") : t("tag.create")}
              </Button>
              <Button type="button" variant="outline" onClick={() => setTagOpen(false)} className="h-12 rounded-sm font-semibold">{t("common.cancel")}</Button>
            </div>
          </form>
        </DialogContent>
      </Dialog>

      <AlertDialog open={!!deleteTag} onOpenChange={(o) => !o && setDeleteTag(null)}>
        <AlertDialogContent className="rounded-sm">
          <AlertDialogHeader>
            <AlertDialogTitle className="font-heading text-2xl">{t("del.tag.title")}</AlertDialogTitle>
            <AlertDialogDescription>„{deleteTag?.name}" — {t("del.tag.desc")}</AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel className="rounded-sm">{t("common.cancel")}</AlertDialogCancel>
            <AlertDialogAction onClick={confirmDeleteTag} data-testid="confirm-delete-tag-button" className="rounded-sm bg-destructive hover:bg-destructive/90">{t("del.confirm")}</AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
      <Footer />
    </div>
  );
}
